import TextField from '@mui/material/TextField';
import { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { NEW_PRODUCT_RESET } from '../../constants/productConstants';
import { createProduct, clearErrors } from '../../actions/productAction';
import { getAllBrands, createBrand, updateBrand } from '../../actions/brandAction';
import { getCategories } from '../../actions/categoryAction';
import ImageIcon from '@mui/icons-material/Image';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useTheme, styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import Autocomplete from '@mui/material/Autocomplete';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { CREATE_BRAND_RESET, UPDATE_BRAND_RESET } from '../../constants/brandConstants';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const flattenCategories = (categories) => {
    const flatCategories = [];
    const traverse = (category, level) => {
        flatCategories.push({
            ...category,
            name: `${'—'.repeat(level)} ${category.name}`,
        });
        if (category.children) {
            category.children.forEach(child => traverse(child, level + 1));
        }
    };
    categories.forEach(category => traverse(category, 0));
    return flatCategories;
};

const NewProduct = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const theme = useTheme();

    const { loading, success, error } = useSelector((state) => state.newProduct);
    const { brands } = useSelector((state) => state.brands);
    const { categories } = useSelector((state) => state.categories);
    const { loading: brandLoading, success: brandSuccess, error: brandError, brand: newBrand } = useSelector((state) => state.newBrand);
    const { loading: updateBrandLoading, isUpdated, error: updateBrandError } = useSelector((state) => state.brandDetails);

    const [highlights, setHighlights] = useState([]);
    const [highlightInput, setHighlightInput] = useState("");
    const [specs, setSpecs] = useState([]);
    const [specsInput, setSpecsInput] = useState({
        title: "",
        description: ""
    });

    const [sizes, setSizes] = useState([]);
    const [sizeInput, setSizeInput] = useState("");

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState(0);
    const [mrp, setMrp] = useState(0);
    const [deliveryCharges, setDeliveryCharges] = useState(0);
    const [category, setCategory] = useState(null);
    const [stock, setStock] = useState(0);
    const [warranty, setWarranty] = useState(0);
    const [brand, setBrand] = useState(null);
    const [images, setImages] = useState([]);
    const [imagesPreview, setImagesPreview] = useState([]);

    const [open, setOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState("");
    const [newBrandLogo, setNewBrandLogo] = useState("");
    const [newBrandLogoPreview, setNewBrandLogoPreview] = useState("");
    const [editingBrand, setEditingBrand] = useState(false);

    const [editedHighlight, setEditedHighlight] = useState(-1);
    const [editedSpec, setEditedSpec] = useState(-1);

    const handleClose = () => {
        setOpen(false);
    };

    const handleNewBrandLogoChange = (e) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                setNewBrandLogoPreview(reader.result);
                setNewBrandLogo(reader.result);
            }
        };

        reader.readAsDataURL(e.target.files[0]);
    }

    const handleCreateBrand = () => {
        if (!newBrandName.trim() || !newBrandLogo) {
            enqueueSnackbar("Brand Name and Logo are required", { variant: "warning" });
            return;
        }
        dispatch(createBrand({ name: newBrandName, logo: newBrandLogo }));
    }

    const handleUpdateBrand = () => {
        if (!newBrandName.trim()) {
            enqueueSnackbar("Brand Name is required", { variant: "warning" });
            return;
        }
        const brandData = {
            name: newBrandName,
        }
        if (newBrandLogo) {
            brandData.logo = newBrandLogo
        }
        dispatch(updateBrand(brand._id, brandData));
    }

    const handleSpecsChange = (e) => {
        setSpecsInput({ ...specsInput, [e.target.name]: e.target.value });
    }

    const addSpecs = () => {
        if (!specsInput.title.trim() || !specsInput.description.trim()) return;
        if (editedSpec !== -1) {
            const updatedSpecs = [...specs];
            updatedSpecs[editedSpec] = specsInput;
            setSpecs(updatedSpecs);
            setEditedSpec(-1);
        } else {
            setSpecs([...specs, specsInput]);
        }
        setSpecsInput({ title: "", description: "" });
    }

    const editSpec = (index) => {
        setEditedSpec(index);
        setSpecsInput(specs[index]);
    }

    const addHighlight = () => {
        if (!highlightInput.trim()) return;
        if (editedHighlight !== -1) {
            const updatedHighlights = [...highlights];
            updatedHighlights[editedHighlight] = highlightInput;
            setHighlights(updatedHighlights);
            setEditedHighlight(-1);
        } else {
            setHighlights([...highlights, highlightInput]);
        }
        setHighlightInput("");
    }

    const editHighlight = (index) => {
        setEditedHighlight(index);
        setHighlightInput(highlights[index]);
    }

    const deleteHighlight = (index) => {
        setHighlights(highlights.filter((h, i) => i !== index))
    }

    const deleteSpec = (index) => {
        setSpecs(specs.filter((s, i) => i !== index))
    }

    const addSize = () => {
        if (!sizeInput.trim()) return;
        setSizes([...sizes, sizeInput]);
        setSizeInput("");
    }

    const deleteSize = (index) => {
        setSizes(sizes.filter((s, i) => i !== index));
    }

    const deleteImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
        setImagesPreview(imagesPreview.filter((_, i) => i !== index));
    };

    const handleProductImageChange = (e) => {
        const newFiles = Array.from(e.target.files);

        const fileReadPromises = newFiles.map(file => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    if (reader.readyState === 2) {
                        resolve(reader.result);
                    }
                };
                reader.readAsDataURL(file);
            });
        });

        Promise.all(fileReadPromises).then(results => {
            setImages((oldImages) => [...oldImages, ...results]);
            setImagesPreview((oldImagesPreview) => [...oldImagesPreview, ...results]);
        });
    };

    const newProductSubmitHandler = (e) => {
        e.preventDefault();

        // required field checks
        if (!brand) {
            enqueueSnackbar("Select Brand", { variant: "warning" });
            return;
        }
        if (!category) {
            enqueueSnackbar("Select Category", { variant: "warning" });
            return;
        }
        if (images.length <= 0) {
            enqueueSnackbar("Add Product Images", { variant: "warning" });
            return;
        }

        const formData = new FormData();

        formData.set("name", name);
        formData.set("description", description);
        formData.set("price", price);
        formData.set("mrp", mrp);
        formData.set("deliveryCharges", deliveryCharges);
        formData.set("category", category._id);
        formData.set("stock", stock);
        formData.set("warranty", warranty);
        formData.set("brand", brand._id);

        images.forEach((image) => {
            formData.append("images", image);
        });

        highlights.forEach((h) => {
            formData.append("highlights", h);
        });

        sizes.forEach((s) => {
            formData.append("sizes", s);
        });

        specs.forEach((s) => {
            formData.append("specifications", JSON.stringify(s));
        });

        dispatch(createProduct(formData));
    }

    useEffect(() => {
        dispatch(getAllBrands());
        dispatch(getCategories());
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (success) {
            enqueueSnackbar("Product Created", { variant: "success" });
            dispatch({ type: NEW_PRODUCT_RESET });
            navigate("/admin/products");
        }
        if (brandError) {
            enqueueSnackbar(brandError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (brandSuccess) {
            enqueueSnackbar("Brand Created", { variant: "success" });
            setOpen(false);
            dispatch({ type: CREATE_BRAND_RESET });
            dispatch(getAllBrands());
            setBrand(newBrand);
        }
        if (updateBrandError) {
            enqueueSnackbar(updateBrandError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isUpdated) {
            enqueueSnackbar("Brand Updated", { variant: "success" });
            setOpen(false);
            dispatch({ type: UPDATE_BRAND_RESET });
            dispatch(getAllBrands());
            setBrand(null);
        }
    }, [dispatch, error, success, navigate, enqueueSnackbar, brandError, brandSuccess, newBrand, isUpdated, updateBrandError]);

    const flattenedCategories = categories ? flattenCategories(categories) : [];

    return (
        <>
            <MetaData title="Admin: New Product | Flipkart" />

            {loading && <BackdropLoader />}
            <motion.form
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                onSubmit={newProductSubmitHandler}
                encType="multipart/form-data"
                className="flex flex-col sm:flex-row rounded-lg shadow p-4"
                id="mainform"
                style={{ backgroundColor: theme.palette.background.paper }}
            >

                <div className="flex flex-col gap-3 m-2 sm:w-1/2">
                    <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        label="Description"
                        multiline
                        rows={3}
                        required
                        variant="outlined"
                        size="small"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <div className="flex justify-between">
                        <TextField
                            label="Price"
                            type="number"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                inputProps: {
                                    min: 0
                                }
                            }}
                            required
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                        />
                        <TextField
                            label="M.R.P."
                            type="number"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                inputProps: {
                                    min: 0
                                }
                            }}
                            required
                            value={mrp}
                            onChange={(e) => setMrp(e.target.value)}
                        />
                    </div>
                    <div className="flex justify-between gap-4">
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={flattenedCategories}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            value={category}
                            onChange={(event, newValue) => {
                                setCategory(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    variant="outlined"
                                    required
                                />
                            )}
                        />
                        <TextField
                            label="Stock"
                            type="number"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                inputProps: {
                                    min: 0
                                }
                            }}
                            required
                            value={stock}
                            onChange={(e) => setStock(e.target.value)}
                        />
                        <TextField
                            label="Warranty"
                            type="number"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                inputProps: {
                                    min: 0
                                }
                            }}
                            required
                            value={warranty}
                            onChange={(e) => setWarranty(e.target.value)}
                        />
                        <TextField
                            label="Delivery Charges"
                            type="number"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                inputProps: {
                                    min: 0
                                }
                            }}
                            required
                            value={deliveryCharges}
                            onChange={(e) => setDeliveryCharges(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center border rounded" style={{ borderColor: theme.palette.divider }}>
                            <input value={highlightInput} onChange={(e) => setHighlightInput(e.target.value)} type="text" placeholder="Highlight" className="px-2 flex-1 outline-none border-none" style={{ backgroundColor: 'transparent', color: theme.palette.text.primary }} />
                            <span onClick={addHighlight} className="py-2 px-6 rounded-r hover:shadow-lg cursor-pointer" style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
                                {editedHighlight !== -1 ? "Update" : "Add"}
                            </span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            {highlights.map((h, i) => (
                                <div key={i} className="flex justify-between rounded items-center py-1 px-2" style={{ backgroundColor: theme.palette.mode === 'light' ? '#E6F4EA' : '#2C3E50' }}>
                                    <p className="text-sm font-medium" style={{ color: theme.palette.success.main }}>{h}</p>
                                    <div className="flex items-center">
                                        <span onClick={() => editHighlight(i)} className="p-1 rounded-full cursor-pointer" style={{ color: theme.palette.primary.main }}>
                                            <EditIcon sx={{ fontSize: 20 }} />
                                        </span>
                                        <span onClick={() => deleteHighlight(i)} className="p-1 rounded-full cursor-pointer" style={{ color: theme.palette.error.main }}>
                                            <DeleteIcon sx={{ fontSize: 20 }} />
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <h2 className="font-medium">Brand Details</h2>
                    <div className="flex justify-between gap-4 items-start">
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={brands ? [{ name: "Add New Brand", isAddNew: true }, ...brands] : [{ name: "Add New Brand", isAddNew: true }]}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            value={brand}
                            onChange={(event, newValue) => {
                                if (newValue && newValue.isAddNew) {
                                    setEditingBrand(false);
                                    setOpen(true);
                                } else {
                                    setBrand(newValue);
                                }
                            }}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option.isAddNew ? (
                                        <span style={{ fontStyle: 'italic' }}>{option.name}</span>
                                    ) : (
                                        <div className="flex items-center">
                                            {option.logo && <img src={option.logo.url} alt={option.name} style={{ width: 30, height: 30, marginRight: 10, objectFit: 'contain' }} />}
                                            {option.name}
                                        </div>
                                    )}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Brand"
                                    variant="outlined"
                                    required
                                />
                            )}
                        />
                        <IconButton onClick={() => {
                            if (brand) {
                                setEditingBrand(true);
                                setNewBrandName(brand.name);
                                setNewBrandLogoPreview(brand.logo.url);
                                setOpen(true);
                            }
                        }} disabled={!brand}>
                            <EditIcon />
                        </IconButton>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center border rounded" style={{ borderColor: theme.palette.divider }}>
                            <input value={sizeInput} onChange={(e) => setSizeInput(e.target.value)} type="text" placeholder="Size" className="px-2 flex-1 outline-none border-none" style={{ backgroundColor: 'transparent', color: theme.palette.text.primary }} />
                            <span onClick={addSize} className="py-2 px-6 rounded-r hover:shadow-lg cursor-pointer" style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
                                Add
                            </span>
                        </div>

                        <div className="flex flex-wrap gap-1.5">
                            {sizes.map((s, i) => (
                                <div key={i} className="flex justify-between rounded items-center py-1 px-2" style={{ backgroundColor: theme.palette.mode === 'light' ? '#E6F4EA' : '#2C3E50' }}>
                                    <p className="text-sm font-medium" style={{ color: theme.palette.success.main }}>{s}</p>
                                    <span onClick={() => deleteSize(i)} className="p-1 rounded-full cursor-pointer" style={{ color: theme.palette.error.main }}>
                                        <DeleteIcon sx={{ fontSize: 20 }} />
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                <div className="flex flex-col gap-2 m-2 sm:w-1/2">
                    <h2 className="font-medium">Specifications</h2>

                    <div className="flex justify-evenly gap-2 items-center">
                        <TextField value={specsInput.title} onChange={handleSpecsChange} name="title" label="Name" placeholder="Model No" variant="outlined" size="small" />
                        <TextField value={specsInput.description} onChange={handleSpecsChange} name="description" label="Description" placeholder="WJDK42DF5" variant="outlined" size="small" />
                        <span onClick={addSpecs} className="py-2 px-6 rounded hover:shadow-lg cursor-pointer" style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
                            {editedSpec !== -1 ? "Update" : "Add"}
                        </span>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        {specs.map((spec, i) => (
                            <div key={i} className="flex justify-between items-center text-sm rounded py-1 px-2" style={{ backgroundColor: theme.palette.mode === 'light' ? '#E3F2FD' : '#152238', color: theme.palette.text.primary }}>
                                <p className="font-medium" style={{ color: theme.palette.text.secondary }}>{spec.title}</p>
                                <p>{spec.description}</p>
                                <div className="flex items-center">
                                    <span onClick={() => editSpec(i)} className="p-1 rounded-full cursor-pointer" style={{ color: theme.palette.primary.main }}>
                                        <EditIcon sx={{ fontSize: 20 }} />
                                    </span>
                                    <span onClick={() => deleteSpec(i)} className="p-1 rounded-full cursor-pointer" style={{ color: theme.palette.error.main }}>
                                        <DeleteIcon sx={{ fontSize: 20 }} />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col gap-2">
                        <h2 className="font-medium">Product Images</h2>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                            {imagesPreview.map((image, i) => (
                                <div key={i} className="relative w-32 h-32 flex-shrink-0">
                                    <img draggable="false" src={image} alt="Product" className="w-full h-full object-cover rounded-lg" />
                                    <span onClick={() => deleteImage(i)} className="absolute top-1 right-1 bg-red-500 p-1 rounded-full text-white cursor-pointer hover:bg-red-600">
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                    </span>
                                </div>
                            ))}
                        </div>
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={<ImageIcon />}
                            sx={{ width: 'fit-content' }}
                        >
                            Upload Images
                            <VisuallyHiddenInput
                                type="file"
                                name="images"
                                accept="image/*"
                                multiple
                                onChange={handleProductImageChange}
                            />
                        </Button>
                    </div>

                    <div className="flex justify-end">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                type="submit"
                                form="mainform"
                                size="large"
                                sx={{ bgcolor: theme.palette.primary.main, width: '10rem' }}
                            >
                                Submit
                            </Button>
                        </motion.div>
                    </div>

                </div>

            </motion.form>

            <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                <DialogTitle sx={{ pb: 1 }}>{editingBrand ? "Edit Brand" : "Add New Brand"}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Brand Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 100, height: 100, border: '2px dashed grey', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {newBrandLogoPreview ? 
                                <img draggable="false" src={newBrandLogoPreview} alt="Brand Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : 
                                <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                            }
                        </Box>
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={<ImageIcon />}
                        >
                            Upload Logo
                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleNewBrandLogoChange} />
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={editingBrand ? handleUpdateBrand : handleCreateBrand} disabled={brandLoading || updateBrandLoading} variant="contained">{editingBrand ? "Update" : "Add"}</Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

export default NewProduct;
